import dayjs from "dayjs";
import {
  bookingStatusLabels,
  BookingStatus,
  type Agent,
  type Booking,
  type BookingType,
  type Company,
  type Container,
  type DeliveryInfo,
  type InvoiceInfo,
  type Port,
  type TransportType,
} from "../types/booking";
import { formatDate } from "./bookingUtils";
import {
  isStorageOption,
  STORAGE_OPTION_LABELS,
} from "./storageOptionUtils";
import { isServiceLevel, SERVICE_LEVEL_LABELS } from "./serviceLevelUtils";
import { normalizeBookingCiAnalysisSummary } from "./agentBookingCiAnalysis";
import { downloadCsv } from "./adminTableCsv";

/** Every admin bookings column except Actions. Hidden columns are included. */
export const ADMIN_BOOKING_EXPORT_COLUMN_KEYS = [
  "id",
  "created_at",
  "reference",
  "company",
  "status",
  "transport_type",
  "booking_type",
  "service_level",
  "origin",
  "destination",
  "etd",
  "eta",
  "container",
  "master_container",
  "products",
  "delivery",
  "storage_option",
  "postal_code",
  "city",
  "sendcloud",
  "leancustoms",
  "purchase_orders",
  "ci_validity",
  "agent",
  "invoice",
] as const;

function first<T>(value: T | T[] | null | undefined): T | undefined {
  if (value == null) return undefined;
  return Array.isArray(value) ? value[0] : value;
}

function named(
  value:
    | { name?: string | null }
    | { name?: string | null }[]
    | null
    | undefined,
): string {
  return first(value)?.name || "";
}

function containerOf(booking: Booking): Container | undefined {
  return first(booking.container_info);
}

function portName(port: Port | Port[] | null | undefined): string {
  return first(port)?.name || "";
}

function deliveryOf(booking: Booking): DeliveryInfo | undefined {
  return first(booking.booking_delivery_info);
}

function hasDelivery(booking: Booking): boolean {
  const delivery = booking.booking_delivery_info;
  if (!delivery) return false;
  if (Array.isArray(delivery)) return delivery.length > 0;
  return true;
}

function formatAddress(delivery: DeliveryInfo): string {
  return [
    delivery.street_name,
    delivery.house_number,
    delivery.postal_code,
    delivery.city,
    delivery.country,
  ]
    .filter(Boolean)
    .join(", ");
}

function statusLabel(status: string | null): string {
  if (!status) return "";
  if (status in bookingStatusLabels) {
    return bookingStatusLabels[status as BookingStatus];
  }
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function bookingIdLabel(booking: Booking): string {
  const abbr = first(booking.transport_type_info as TransportType | TransportType[])
    ?.abbreviation;
  return `EFF${booking.id}${abbr || ""}`;
}

function originLabel(booking: Booking): string {
  const container = containerOf(booking);
  const containerOrigin = portName(container?.container_origin_port);
  if (containerOrigin) return containerOrigin;
  return portName(booking.origin_port_info) || named(booking.origin_port_info);
}

function destinationLabel(booking: Booking): string {
  const container = containerOf(booking);
  const containerDest = portName(container?.container_destination_port);
  if (containerDest) return containerDest;
  return (
    portName(booking.destination_port_info) ||
    named(booking.destination_port_info)
  );
}

function etdLabel(booking: Booking): string {
  const container = containerOf(booking);
  const effective = container?.delayed_etd || container?.etd;
  if (!effective) return "";
  const formatted = formatDate(effective);
  return container?.delayed_etd ? `${formatted} (Delayed)` : formatted;
}

function customerEtaLabel(booking: Booking): string {
  if (booking.eta) return `${formatDate(booking.eta)} (Override)`;
  const customerEta = containerOf(booking)?.eta_customer;
  return customerEta ? formatDate(customerEta) : "";
}

function containerLabel(booking: Booking): string {
  const container = containerOf(booking);
  if (!container) return "";
  return container.reference || (container.id ? `#${container.id}` : "");
}

function billOfLadingLabel(booking: Booking): string {
  const container = containerOf(booking);
  const link = first(container?.master_container_info);
  const master = first(link?.master_containers);
  if (!master) return "";
  return master.reference || `#${master.id}`;
}

function productCount(booking: Booking): string {
  const products = booking.product_info;
  if (!Array.isArray(products)) return "";
  return products.length > 0 ? String(products.length) : "";
}

function deliveryLabel(booking: Booking): string {
  if (!hasDelivery(booking)) return "In opslag";
  const delivery = deliveryOf(booking);
  return delivery ? formatAddress(delivery) : "Delivery";
}

function storageOptionLabel(booking: Booking): string {
  if (hasDelivery(booking)) return "";
  if (booking.storage_option && isStorageOption(booking.storage_option)) {
    return STORAGE_OPTION_LABELS[booking.storage_option];
  }
  return "";
}

function sendcloudLabel(booking: Booking): string {
  const shipments = booking.sendcloud_shipments;
  const list = Array.isArray(shipments)
    ? shipments
    : shipments
      ? [shipments]
      : [];
  if (list.length === 0) return "";
  const withLabel = list.filter((shipment) => shipment.label_url).length;
  return withLabel > 0
    ? `${list.length} shipment(s), ${withLabel} with label`
    : `${list.length} shipment(s)`;
}

function leanCustomsLabel(booking: Booking): string {
  const parts: string[] = [];
  if (booking.leancustoms_order_id) {
    parts.push(`Order ${booking.leancustoms_order_id}`);
  }
  if (booking.leancustoms_declaration_id) {
    parts.push(`Declaration ${booking.leancustoms_declaration_id}`);
  }
  return parts.join("; ");
}

function purchaseOrderLabel(booking: Booking): string {
  const orders = booking.booking_purchase_orders;
  const count = Array.isArray(orders) ? orders.length : 0;
  return count > 0 ? String(count) : "";
}

function ciLabel(booking: Booking): string {
  const analysis = normalizeBookingCiAnalysisSummary(
    booking.commercial_invoice_analysis,
  );
  if (!analysis) return "";
  const parts = [`${analysis.completeness_score}% complete`];
  if (analysis.document_type) parts.push(analysis.document_type);
  return parts.join("; ");
}

function invoiceLabel(booking: Booking): string {
  const invoice = first(booking.invoice_info) as InvoiceInfo | undefined;
  if (!invoice) return "";
  const total = (invoice.lines || []).reduce((sum, line) => {
    const lineTotal = (line.quantity || 0) * (line.unit_price || 0);
    const vat = lineTotal * ((line.vat_percentage || 0) / 100);
    return sum + lineTotal + vat;
  }, 0);
  const status = invoice.moneybird_payment_status || "unknown";
  return `#${invoice.id} ${invoice.currency || ""} ${total.toFixed(2)} ${status}`.trim();
}

const HEADERS = [
  "ID",
  "Created",
  "Reference",
  "Company",
  "Status",
  "Transport",
  "Booking Type",
  "Service Level",
  "Origin",
  "Destination",
  "ETD",
  "Customer ETA",
  "Container",
  "Bill of lading (B/L)",
  "Products",
  "Delivery",
  "Storage Option",
  "Postal code",
  "City",
  "SendCloud",
  "Lean Customs",
  "POs",
  "CI",
  "Agent",
  "Invoice",
];

function bookingToRow(booking: Booking): string[] {
  const delivery = deliveryOf(booking);
  const serviceLevel =
    booking.service_level && isServiceLevel(booking.service_level)
      ? SERVICE_LEVEL_LABELS[booking.service_level]
      : "";

  return [
    bookingIdLabel(booking),
    booking.created_at ? formatDate(booking.created_at) : "",
    booking.reference || "",
    named(booking.company_info as Company | Company[]),
    statusLabel(booking.status),
    named(booking.transport_type_info as TransportType | TransportType[]),
    named(booking.booking_type_info as BookingType | BookingType[]),
    serviceLevel,
    originLabel(booking),
    destinationLabel(booking),
    etdLabel(booking),
    customerEtaLabel(booking),
    containerLabel(booking),
    billOfLadingLabel(booking),
    productCount(booking),
    deliveryLabel(booking),
    storageOptionLabel(booking),
    delivery?.postal_code || "",
    delivery?.city || "",
    sendcloudLabel(booking),
    leanCustomsLabel(booking),
    purchaseOrderLabel(booking),
    ciLabel(booking),
    named(booking.agent_info as Agent | Agent[]),
    invoiceLabel(booking),
  ];
}

export function downloadAdminBookingsCsv(
  bookings: Booking[],
  filename = `bookings-${dayjs().format("YYYY-MM-DD")}.csv`,
): void {
  downloadCsv(
    HEADERS,
    bookings.map(bookingToRow),
    filename,
  );
}
